import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import {
  Modal,
  Button,
  Form,
  Table,
  Pagination,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import { FaEye, FaEdit } from "react-icons/fa";
import { RiDeleteBin5Fill } from "react-icons/ri";
import { BsThreeDots } from "react-icons/bs";
import {
  MdAirplanemodeInactive,
  MdAirplanemodeActive,
  MdOutlineUpcoming,
  MdUpcoming,
} from "react-icons/md";
import { MdFilterAlt } from "react-icons/md";
import { MdFilterAltOff } from "react-icons/md";

const API_URL = process.env.REACT_APP_API_URL;

function ActiveChannelList() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const searchTimeoutRef = useRef(null);
  const [editFormData, setEditFormData] = useState({
    id: "",
    username: "",
    email: "",
    mobile: "",
    whatsapp_number: "",
  });

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState({
    title: "",
    text: "",
    type: "",
    confirmAction: null,
  });

  const showCustomMessageModal = (title, text, type, confirmAction = null) => {
    setMessageModalContent({ title, text, type, confirmAction });
    setShowMessageModal(true);
  };

  const closeCustomMessageModal = () => {
    setShowMessageModal(false);
    setMessageModalContent({
      title: "",
      text: "",
      type: "",
      confirmAction: null,
    });
  };

  const getAuthToken = () => {
    return localStorage.getItem("token");
  };

  const [isFilterActive, setIsFilterActive] = useState(false);
  const handleToggle = () => {
    setIsFilterActive(!isFilterActive);
  };

  const fetchChannels = async (page = 1, name = "", location = "") => {
    setError(null);
    try {
      const token = getAuthToken();
      if (!token) {
        showCustomMessageModal(
          "Authentication Error",
          "Authentication token not found. Please log in.",
          "error",
        );
        throw new Error("Authentication token not found. Please log in.");
      }

      const response = await fetch(
        `${API_URL}/Channel-list-active?page=${page}&limit=10&mobile=${name}&parent_id=${location}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        if (response.status === 401) {
          showCustomMessageModal(
            "Authorization Error",
            "Unauthorized: Please log in again.",
            "error",
          );
          throw new Error("Unauthorized: Please log in again.");
        }
        const errorData = await response.json();
        showCustomMessageModal(
          "Error",
          errorData.message || "Failed to fetch Channel.",
          "error",
        );
        throw new Error(errorData.message || "Failed to fetch Channel.");
      }
      const data = await response.json();
      setChannels(data.data || []);
      setTotalPages(data.totalPages || 1);
      setCurrentPage(page);
    } catch (err) {
      console.error("Fetch Channel error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChannels(currentPage);
  }, [currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleViewChannels = async (channelId) => {
    setLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      if (!token) {
        showCustomMessageModal(
          "Authentication Error",
          "Authentication token not found. Please log in.",
          "error",
        );
        throw new Error("Authentication token not found. Please log in.");
      }

      const response = await fetch(`${API_URL}/Channel-view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: channelId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showCustomMessageModal(
          "Error",
          errorData.message || "Failed to fetch Channel details.",
          "error",
        );
        throw new Error(
          errorData.message || "Failed to fetch Channel details.",
        );
      }

      const data = await response.json();
      setSelectedChannels(data.data);
      setShowViewModal(true);
    } catch (err) {
      console.error("View Channel error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChannels = async (channelId) => {
    setLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please log in.");
      }

      const response = await fetch(`${API_URL}/Channel-view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: channelId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showCustomMessageModal(
          "Error",
          errorData.message || "Failed to fetch Channel for editing.",
          "error",
        );
        throw new Error(
          errorData.message || "Failed to fetch Channel for editing.",
        );
      }

      const data = await response.json();
      const channelData = data.data;

      setSelectedChannels(channelData);

      setEditFormData({
        id: channelData.id || "",
        username: channelData.username || "",
        email: channelData.email || "",
        mobile: channelData.mobile || "",
        whatsapp_number: channelData.whatsapp_number || "",
      });

      setShowEditModal(true);
    } catch (err) {
      console.error("Edit Channel error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUpdateChannel = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        showCustomMessageModal(
          "Authentication Error",
          "Authentication token not found. Please log in.",
          "error",
        );
        throw new Error("Authentication token not found. Please log in.");
      }

      const payload = { ...selectedChannels };
      payload.id = editFormData.id;
      payload.username = editFormData.username;
      payload.email = editFormData.email;
      payload.mobile = editFormData.mobile;
      payload.whatsapp_number = editFormData.whatsapp_number;

      const response = await fetch(`${API_URL}/Channel-update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showCustomMessageModal(
          "Error",
          errorData.message || "Failed to update Channel.",
          "error",
        );
        throw new Error(errorData.message || "Failed to update Channel.");
      }

      const result = await response.json();
      if (result.success == "1") {
        showCustomMessageModal(
          "Success",
          "Channel has been updated successfully!",
          "success",
        );
        setShowEditModal(false);
        fetchChannels(currentPage);
      } else {
        showCustomMessageModal(
          "Error",
          result.message || "Something went wrong",
          "error",
        );
      }
    } catch (err) {
      console.error("Update Channel error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (channelId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    showCustomMessageModal(
      "Confirm Status Change",
      `Do you want to change the status to ${newStatus}?`,
      "warning",
      async () => {
        setLoading(true);
        setError(null);
        try {
          const token = getAuthToken();
          if (!token) {
            showCustomMessageModal(
              "Authentication Error",
              "Authentication token not found. Please log in.",
              "error",
            );
            throw new Error("Authentication token not found. Please log in.");
          }

          const response = await fetch(`${API_URL}/Channel-update-status`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              id: channelId,
              status: newStatus,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            showCustomMessageModal(
              "Error",
              errorData.message || "Failed to update Channel status.",
              "error",
            );
            throw new Error(
              errorData.message || "Failed to update Channel status.",
            );
          }

          const result = await response.json();
          if ((result.success = "1")) {
            showCustomMessageModal(
              "Success",
              "Channel status has been updated successfully!",
              "success",
            );
            fetchChannels(currentPage);
          } else {
            showCustomMessageModal(
              "Error",
              result.message || "Something went wrong",
              "error",
            );
          }
        } catch (err) {
          console.error("Status update error:", err);
        } finally {
          setLoading(false);
        }
      },
    );
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedChannels(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedChannels(null);
    setEditFormData({
      id: "",
      username: "",
      email: "",
      mobile: "",
      whatsapp_number: "",
    });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchName(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchChannels(1, value, searchLocation);
    }, 500);
  };

  const handleSearchChangeLocation = (e) => {
    const value = e.target.value;
    setSearchLocation(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchChannels(1, searchName, value);
    }, 500);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          fontFamily: "'Poppins', 'DM Sans', sans-serif",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#64748b",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid #cbd5e1",
              borderTop: "3px solid #2b6cb0",
              borderRadius: "50%",
              display: "inline-block",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <p
            style={{
              marginTop: "16px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Loading Channels...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          fontFamily: "'Poppins', 'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "1600px", margin: "0 auto" }}>
          {/* 🎯 HEADER CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: "24px",
                boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                flexShrink: 0,
              }}
            >
              📺
            </div>
            <div style={{ flex: 1, minWidth: "220px" }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: "22px",
                  fontWeight: "800",
                  color: "#1e3a5f",
                  letterSpacing: "-0.5px",
                }}
              >
                Active Channel List
              </h1>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Manage all active channel partners
              </p>
            </div>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <Link
                to="/create-channel"
                style={{
                  padding: "12px 18px",
                  borderRadius: "12px",
                  border: "none",
                  background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                  color: "#ffffff",
                  fontWeight: "700",
                  fontSize: "14px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 4px 12px rgba(30, 87, 153, 0.25)",
                  textDecoration: "none",
                }}
              >
                <FaPlus /> Partner
              </Link>

              <button
                onClick={handleToggle}
                style={{
                  padding: "12px 16px",
                  borderRadius: "12px",
                  border: "1.5px solid #e2e8f0",
                  background: isFilterActive ? "#1e5799" : "#ffffff",
                  color: isFilterActive ? "#ffffff" : "#1e3a5f",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Toggle Filters"
              >
                {isFilterActive ? <MdFilterAltOff /> : <MdFilterAlt />}
              </button>
            </div>
          </div>

          {/* 🎯 MAIN CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "5px",
                background: "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
              }}
            />

            {/* Filters */}
            {isFilterActive && (
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <Row className="gy-2 align-items-end">
                  <Col md={4}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12.5px",
                        fontWeight: "700",
                        color: "#1e3a5f",
                        marginBottom: "6px",
                      }}
                    >
                      Mobile
                    </label>
                    <input
                      type="text"
                      placeholder="Search Mobile"
                      value={searchName}
                      onChange={handleSearchChange}
                      className="form-control"
                    />
                  </Col>

                  <Col md={4}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12.5px",
                        fontWeight: "700",
                        color: "#1e3a5f",
                        marginBottom: "6px",
                      }}
                    >
                      Parent ID
                    </label>
                    <input
                      type="text"
                      placeholder="Parent ID"
                      value={searchLocation}
                      onChange={handleSearchChangeLocation}
                      className="form-control"
                    />
                  </Col>
                </Row>
              </div>
            )}

            <div style={{ padding: "24px" }}>
              <div className="table-responsive">
                <Table bordered hover>
                  <thead
                    style={{
                      background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                      color: "#ffffff",
                    }}
                  >
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Password</th>
                      <th>KYC Status</th>
                      <th>Parent Name</th>
                      <th>Parent ID</th>
                      <th>Status</th>
                      <th>Date Of Joining</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channels.length > 0 ? (
                      channels.map((channel, i) => (
                        <tr key={channel.id}>
                          <td className="fw-bold">
                            {(currentPage - 1) * 10 + i + 1}
                          </td>
                          <td>
                            {channel.username
                              ? channel.username.charAt(0).toUpperCase() +
                                channel.username.slice(1).toLowerCase()
                              : ""}
                          </td>

                          <td>{channel.mobile}</td>
                          <td>{channel.password}</td>
                          <td
                            style={{
                              color:
                                channel.kyc?.toLowerCase() === "success"
                                  ? "green"
                                  : channel.kyc?.toLowerCase() === "pending"
                                    ? "orange"
                                    : "red",
                              fontWeight: "600",
                            }}
                          >
                            {channel.kyc
                              ? channel.kyc.charAt(0).toUpperCase() +
                                channel.kyc.slice(1).toLowerCase()
                              : "-"}
                          </td>

                          <td>
                            {channel.parent_name
                              ? channel.parent_name.charAt(0).toUpperCase() +
                                channel.parent_name.slice(1).toLowerCase()
                              : "-"}
                          </td>

                          <td>{channel.parent_id}</td>
                          <td>
                            <span
                              className={`badge ${
                                channel.status === "active"
                                  ? "bg-success"
                                  : "bg-danger"
                              }`}
                            >
                              {channel.status}
                            </span>
                          </td>

                          <td>
                            {channel.date
                              ? (() => {
                                  const [day, month, year] =
                                    channel.date.split("-");
                                  return `${day}-${month}-${year}`;
                                })()
                              : "-"}
                          </td>
                          <td>
                            <div className="dropdown">
                              <button
                                className="btn light btn-action dropdown-toggle"
                                type="button"
                                id="dropdownMenuButton"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                style={{
                                  borderRadius: "8px",
                                  fontWeight: 600,
                                }}
                              >
                                <BsThreeDots size={20} />
                              </button>
                              <ul
                                className="dropdown-menu"
                                aria-labelledby="dropdownMenuButton"
                              >
                                <li className="dropdown-item">
                                  <button
                                    className="btn view_btn btn-sm"
                                    onClick={() =>
                                      handleViewChannels(channel.id)
                                    }
                                    title="View Channel Details"
                                    style={{
                                      borderRadius: "8px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    <FaEye /> View
                                  </button>
                                </li>
                                <li className="dropdown-item">
                                  <button
                                    className="btn view_btn btn-sm"
                                    title="Edit Channel"
                                    onClick={() =>
                                      handleEditChannels(channel.id)
                                    }
                                    style={{
                                      borderRadius: "8px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    <FaEdit /> Edit
                                  </button>
                                </li>
                                <li className="dropdown-item">
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() =>
                                      handleStatusUpdate(
                                        channel.id,
                                        channel.status,
                                      )
                                    }
                                    style={{
                                      borderRadius: "8px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {channel.status === "active" ? (
                                      <MdAirplanemodeInactive />
                                    ) : (
                                      <MdAirplanemodeActive />
                                    )}

                                    {channel.status === "active"
                                      ? "Deactivate"
                                      : "Activate"}
                                  </button>
                                </li>
                                <li className="dropdown-item">
                                  <a
                                    href={`https://dashboard.rajasthanirealestates.in/login?mobile=${channel.mobile}&pssword=${channel.password}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn view_btn btn-sm"
                                    title="Login"
                                    style={{
                                      borderRadius: "8px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    <FaEdit /> Login
                                  </a>
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="10"
                          className="text-center py-4"
                          style={{
                            color: "#dc2626",
                            fontWeight: "700",
                          }}
                        >
                          No Channel found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-end mt-3">
                <Pagination>
                  <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  />

                  <Pagination.Item
                    active={1 === currentPage}
                    onClick={() => handlePageChange(1)}
                  >
                    1
                  </Pagination.Item>

                  {currentPage > 3 && <Pagination.Ellipsis />}

                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    if (
                      pageNumber > 1 &&
                      pageNumber < totalPages &&
                      Math.abs(pageNumber - currentPage) <= 1
                    ) {
                      return (
                        <Pagination.Item
                          key={pageNumber}
                          active={pageNumber === currentPage}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      );
                    }
                    return null;
                  })}

                  {currentPage < totalPages - 2 && <Pagination.Ellipsis />}

                  {totalPages > 1 && (
                    <Pagination.Item
                      active={totalPages === currentPage}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </Pagination.Item>
                  )}

                  <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p
            style={{
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "12.5px",
              marginTop: "24px",
              fontWeight: "500",
            }}
          >
            © {new Date().getFullYear()} A2 Demo Real Estates Pvt Ltd. All
            Rights Reserved.
          </p>
        </div>
      </div>

      {/* View Channel Modal */}
      <Modal
        show={showViewModal}
        onHide={handleCloseViewModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Channel Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedChannels && (
            <Row>
              <div className="col-md-12">
                <table className="table">
                  <tbody>
                    <tr>
                      <th>Name</th>
                      <td>{selectedChannels.username}</td>
                    </tr>
                    <tr>
                      <th>Email</th>
                      <td>{selectedChannels.email}</td>
                    </tr>
                    <tr>
                      <th>Mobile</th>
                      <td>{selectedChannels.mobile}</td>
                    </tr>
                    <tr>
                      <th>WhatsApp No.</th>
                      <td>{selectedChannels.whatsapp_number}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleCloseViewModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Channel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdateChannel}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="editUsername">
                  <Form.Label
                    style={{
                      fontSize: "12.5px",
                      fontWeight: "700",
                      color: "#1e3a5f",
                      marginBottom: "6px",
                    }}
                  >
                    Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={editFormData.username}
                    onChange={handleEditFormChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="editEmail">
                  <Form.Label
                    style={{
                      fontSize: "12.5px",
                      fontWeight: "700",
                      color: "#1e3a5f",
                      marginBottom: "6px",
                    }}
                  >
                    Email
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditFormChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="editMobile">
                  <Form.Label
                    style={{
                      fontSize: "12.5px",
                      fontWeight: "700",
                      color: "#1e3a5f",
                      marginBottom: "6px",
                    }}
                  >
                    Mobile
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={editFormData.mobile}
                    onChange={handleEditFormChange}
                    maxLength="10"
                    required
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="editWhatsappNumber">
                  <Form.Label
                    style={{
                      fontSize: "12.5px",
                      fontWeight: "700",
                      color: "#1e3a5f",
                      marginBottom: "6px",
                    }}
                  >
                    WhatsApp Number
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="whatsapp_number"
                    value={editFormData.whatsapp_number}
                    maxLength="10"
                    onChange={handleEditFormChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Button
              variant="primary"
              type="submit"
              className="w-100"
              disabled={loading}
              style={{
                borderRadius: "10px",
                fontWeight: 700,
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                border: "none",
                boxShadow: "0 4px 12px rgba(30, 87, 153, 0.25)",
              }}
            >
              {loading ? "Updating..." : "Update Channel"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {showMessageModal && (
        <div
          className="modal d-block modalshowparentdesign"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5 className="modal-title">{messageModalContent.title}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeCustomMessageModal}
                ></button>
              </div>
              <div className="modal-body text-secondary">
                <p>{messageModalContent.text}</p>
              </div>
              <div className="modal-footer justify-content-center">
                {messageModalContent.confirmAction ? (
                  <>
                    <Button
                      variant="primary"
                      onClick={() => {
                        messageModalContent.confirmAction();
                        closeCustomMessageModal();
                      }}
                    >
                      Confirm
                    </Button>
                    <Button variant="danger" onClick={closeCustomMessageModal}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant={
                      messageModalContent.type === "success"
                        ? "success"
                        : messageModalContent.type === "error"
                          ? "danger"
                          : "primary"
                    }
                    onClick={closeCustomMessageModal}
                  >
                    OK
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

export default ActiveChannelList;